/**
 * Settings → Sharing → Connected AI tools (TMMAPP-0142 L1; TMMAPI-0183 U1, MM-D-148). Free, and
 * shown to every tier.
 *
 * - Each connection (`GET /v1/me/agent-connections`) is a row: its name (renamed inline, saved when
 *   the field is left or Enter is pressed; the server's `400` shows at the field), "First used ·
 *   Last used", and its sessions, each with Off / Read only / Read and write (saved at once, then
 *   the list is read again). An archived session's row (`archived`, TMMAPI-0185 U1) is read-only:
 *   its access as text, and "Archived sessions are read-only for AI tools.", as the session's own
 *   dialog says.
 * - **Choose sessions** opens a picker of the person's active sessions (never Help & feedback),
 *   each with the same choice.
 * - **Disconnect** confirms, then `DELETE …/{id}` removes the row.
 * - With no connection, the empty state says how to connect one, and that it sees nothing until a
 *   session is chosen. (No help link: the help browser opens an article only inside Help &
 *   feedback, and the `connected-ai-tools` article isn't in the library yet.)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AGENT_LABEL_MAX,
  agentConnectionsApi,
  type AgentAccessChoice,
  type AgentConnection,
} from '@/api/agentConnections';
import { errorCopy } from '@/api/errorCopy';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { HeadedCard } from '@/components/HeadedCard';
import { useConfirm } from '@/components/useConfirm';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { useBrand } from '@/theme/useBrand';
import type { SettingsNotify } from '@/features/settings/settingsHref';
import { AgentAccessControl } from './AgentAccessControl';
import { ChooseSessionsDialog } from './ChooseSessionsDialog';

export function ConnectedAiToolsCard({ onNotify }: { onNotify: SettingsNotify }) {
  const { t } = useTranslation();
  const { productName } = useBrand();
  const [connections, setConnections] = useState<AgentConnection[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [choosing, setChoosing] = useState<number | null>(null);
  const disconnecting = useConfirm<AgentConnection>();
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setConnections(await agentConnectionsApi.list());
      setLoadError(null);
    } catch (error) {
      setLoadError(error);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  /** A choice saves at once; the list is read again, so every row shows what the server holds. */
  const choose = useCallback(
    async (connectionId: number, sessionId: number, choice: AgentAccessChoice) => {
      await agentConnectionsApi.setAccess(connectionId, sessionId, choice);
      await load();
    },
    [load],
  );

  const picking = connections?.find((row) => row.id === choosing) ?? null;

  let body;
  if (connections === null && loadError !== null) {
    body = (
      <ErrorState error={loadError} what={t('agentTools.loadError')} onRetry={() => void load()} />
    );
  } else if (connections === null) {
    body = <Typography variant="body2">{t('common.loading')}</Typography>;
  } else if (connections.length === 0) {
    body = (
      <Typography variant="body2" data-agent-tools-empty="">
        {t('agentTools.empty', { productName })}
      </Typography>
    );
  } else {
    body = (
      <Stack spacing={2}>
        {connections.map((connection) => (
          <ConnectionRow
            key={connection.id}
            connection={connection}
            onRenamed={(saved) => {
              onNotify(t('agentTools.renamed'));
              setConnections(
                (rows) => rows?.map((row) => (row.id === saved.id ? saved : row)) ?? rows,
              );
            }}
            onChoose={(sessionId, choice) => choose(connection.id, sessionId, choice)}
            onChooseSessions={() => setChoosing(connection.id)}
            onDisconnect={() => disconnecting.ask(connection, `disconnect-${connection.id}`)}
          />
        ))}
      </Stack>
    );
  }

  return (
    <HeadedCard
      id="settings-connected-ai-tools"
      title={t('agentTools.title')}
      subtitle={t('agentTools.subtitle')}
    >
      {body}
      {picking !== null && (
        <ChooseSessionsDialog
          connection={picking}
          onChoose={(sessionId, choice) => choose(picking.id, sessionId, choice)}
          onClose={() => setChoosing(null)}
        />
      )}
      {disconnecting.target !== null && (
        <ConfirmDialog
          title={t('agentTools.disconnectTitle', { label: disconnecting.target.label })}
          consequence={t('agentTools.disconnectConsequence', { productName })}
          confirmLabel={t('agentTools.disconnectConfirm')}
          destructive
          busy={disconnecting.busy}
          error={disconnecting.error}
          onConfirm={() =>
            void disconnecting.run(
              async (target) => {
                await agentConnectionsApi.disconnect(target.id);
                setConnections((rows) => rows?.filter((row) => row.id !== target.id) ?? rows);
                onNotify(t('agentTools.disconnected', { label: target.label }));
              },
              (error) => errorCopy(t, error, 'agentTools.saveError'),
            )
          }
          onCancel={disconnecting.cancel}
        />
      )}
    </HeadedCard>
  );
}

interface ConnectionRowProps {
  connection: AgentConnection;
  onRenamed: (saved: AgentConnection) => void;
  onChoose: (sessionId: number, choice: AgentAccessChoice) => Promise<void>;
  onChooseSessions: () => void;
  onDisconnect: () => void;
}

function ConnectionRow({
  connection,
  onRenamed,
  onChoose,
  onChooseSessions,
  onDisconnect,
}: ConnectionRowProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const locale = useLocale();
  const zone = useMe().user.timezone ?? 'UTC';
  const [name, setName] = useState(connection.label);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const when = (iso: string) => formatInstant(iso, locale, zone, { dateStyle: 'medium' });

  async function rename() {
    const label = name.trim();
    if (label === connection.label || saving) {
      setName(connection.label);
      setNameError(null);
      return;
    }
    setSaving(true);
    try {
      const saved = await agentConnectionsApi.rename(connection.id, label);
      setName(saved.label);
      setNameError(null);
      onRenamed(saved);
    } catch (error) {
      setNameError(errorCopy(t, error, 'agentTools.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      component="section"
      aria-label={connection.label}
      data-agent-connection={connection.id}
      sx={{ border: `1px solid ${tokens.line}`, borderRadius: 1.5, p: 2 }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 1.5, alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <TextField
            size="small"
            label={t('agentTools.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => void rename()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void rename();
              }
            }}
            error={nameError !== null}
            helperText={nameError ?? undefined}
            fullWidth
            sx={{ maxWidth: 360 }}
            slotProps={{
              htmlInput: {
                maxLength: AGENT_LABEL_MAX,
                'aria-label': t('agentTools.nameFor', { label: connection.label }),
                // Saving holds the field read-only, never disabled, so focus stays on it (E6).
                readOnly: saving,
                'aria-busy': saving,
              },
            }}
          />
          <Typography variant="caption" component="p" sx={{ color: tokens.text2, mt: 0.75 }}>
            {t('agentTools.used', {
              first: when(connection.firstSeenAt),
              last: when(connection.lastUsedAt),
            })}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={onDisconnect}
          sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
          aria-label={t('agentTools.disconnectFor', { label: connection.label })}
          data-confirm-opener={`disconnect-${connection.id}`}
        >
          {t('agentTools.disconnect')}
        </Button>
      </Stack>
      <Typography component="h3" sx={{ fontSize: 13, fontWeight: 600, mt: 2, mb: 1 }}>
        {t('agentTools.sessionsHeading')}
      </Typography>
      {connection.grants.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2 }} data-agent-no-sessions="">
          {t('agentTools.noSessions')}
        </Typography>
      ) : (
        <Stack spacing={1.5} data-agent-grants="">
          {connection.grants.map((grant) => (
            <Stack
              key={grant.sessionId}
              direction={{ xs: 'column', md: 'row' }}
              sx={{ gap: 1, alignItems: { md: 'center' }, justifyContent: 'space-between' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {grant.sessionName}
                </Typography>
                {grant.archived && (
                  <Typography
                    sx={{ fontSize: 13, color: tokens.text2 }}
                    data-agent-grant-archived={grant.sessionId}
                  >
                    {t('agentTools.archived')}
                  </Typography>
                )}
              </Box>
              <AgentAccessControl
                label={t('agentTools.accessFor', { session: grant.sessionName })}
                value={grant.access}
                readOnly={grant.archived}
                onChoose={(choice) => onChoose(grant.sessionId, choice)}
              />
            </Stack>
          ))}
        </Stack>
      )}
      <Button size="small" sx={{ mt: 1.5 }} onClick={onChooseSessions}>
        {t('agentTools.chooseSessions')}
      </Button>
    </Box>
  );
}
